"use client";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { MenuItem } from "./Header";
import { DROPDOWN_MAX_WIDTH, DROPDOWN_OFFSET, HEADER_HEIGHT } from "./constants";
import { Inner, Container } from "../Elements";
import { Flex, Grid, Icon, Text } from "@thenounproject/lingo-core";
import { MarketingButton, TextButton } from "../buttons";
import { COLORS, MARKETING_BP_MD } from "../utils/constants";
import Link from "next/link";
import HighlightedItemComponent from "./HighlightedItem";
import { MarketingButtons } from "../MarketingButtons";
import LinkItemComponent from "./LinkItem";
import CtaButtonComponent from "./CtaButtons";

const DropdownWrapper = styled.div<{ $isOpen: boolean; $isScrolled?: boolean }>`
  position: fixed;
  top: ${props => (props.$isScrolled ? 45 : 80)}px;
  left: 49.66%;
  transform: translateX(-50%) translateY(${props => (props.$isOpen ? 0 : "-20px")});
  max-width: ${DROPDOWN_MAX_WIDTH}px;
  width: 95vw;
  background: white;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 10px 10px;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? "visible" : "hidden")};
  pointer-events: ${props => (props.$isOpen ? "auto" : "none")};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease,
    transform 0.3s ease;
  margin-top: ${DROPDOWN_OFFSET}px;
  max-height: "100vh";
  padding-bottom: 40px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 2000;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DropdownContent = styled(Flex)`
  padding: 0 40px 70px 40px;
  flex-direction: column;
  gap: 20px;
  /* max-width: 1000px; */
  /* margin: 0 auto; */
`;

const Wrapper = styled(Container)<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  padding: ${props => (props.isScrolled ? "0px 60px" : "20px 60px")};
  z-index: 1200;
  background: white;
  transition: padding 0.2s ease-out;

  ${Inner} {
    height: ${HEADER_HEIGHT}px;
    justify-content: space-around;
  }

  @media (max-width: ${MARKETING_BP_MD}) {
    padding: ${props => (props.isScrolled ? "0px 24px" : "20px 24px")};
    ${Inner} {
      height: ${HEADER_HEIGHT}px;
      justify-content: space-between;
    }
  }
`;

const DesktopNav = styled(Flex)`
  position: relative;
  align-items: center;
  gap: 32px;

  .desktop-nav-items {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .desktop-nav-button {
    display: none;
  }

  @media (max-width: ${MARKETING_BP_MD}) {
    .desktop-nav-items {
      display: none;
    }
    .desktop-nav-button {
      display: flex;
    }
  }
`;

const LogoLink = styled(Link)`
  width: 75px;
  height: 35px;
`;

const LinkGrid = styled(Grid)`
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
`;

const LinkColumn = styled(Flex)`
  flex-direction: column;
  gap: 28px;
`;

const NavButton = styled(TextButton)<{ isOpen?: boolean }>`
  padding: 12px 12px;
  border-radius: 70px;
  display: flex;
  margin-right: -5px;
  margin-left: -5px;
  align-items: end;
  background: ${props => (props.isOpen ? COLORS.gray : "transparent")};
`;

const MenuButton = styled(TextButton)`
  svg {
    stroke-width: 1.5px;
    stroke: ${COLORS.blue};
  }
`;

export function useIsScrolled() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId = 0;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsScrolled(prev => {
          if (!prev && window.scrollY > 60) return true;
          if (prev && window.scrollY < 10) return false;
          return prev;
        });
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return isScrolled;
}

type Props = {
  navRef: React.RefObject<HTMLElement>;
  menuItems: MenuItem[];
  activeDropdown: string | null;
  setActiveDropdown: (id: string | null) => void;
  showMenu: boolean;
  setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
};
export default function DesktopNavComponent({
  setShowMenu,
  showMenu,
  navRef,
  menuItems,
  activeDropdown,
  setActiveDropdown,
}: Props) {
  const isScrolled = useIsScrolled();
  const handleMobileMenuToggle = useCallback(() => {
    setShowMenu(prev => !prev);
  }, [setShowMenu]);

  const renderDesktopNavItems = useCallback(() => {
    return menuItems.map(item => {
      const isOpen = activeDropdown === item.id;

      if (!item.dropdown) {
        return (
          <NavButton key={item.id} variant="blue" href={item.link} isOpen={isOpen}>
            {item.text}
          </NavButton>
        );
      }

      const hasTitles = item.dropdown.columns.some(column => column.title);
      return (
        <Fragment key={item.id}>
          <NavButton
            variant="blue"
            isOpen={isOpen}
            onClick={() => setActiveDropdown(isOpen ? null : item.id)}
            icon={isOpen ? "navigation.nav-up" : "navigation.nav-down"}
            iconSize={8}>
            {item.text}
          </NavButton>
          <DropdownWrapper $isOpen={isOpen} $isScrolled={isScrolled}>
            <DropdownContent>
              {item.dropdown.highlightedItem && (
                <HighlightedItemComponent item={item.dropdown.highlightedItem} />
              )}
              <LinkGrid>
                {item.dropdown.columns.map((column, index) => (
                  <LinkColumn key={`column-${index}`}>
                    {column.title && (
                      <Text font="marketing.semiBold" color={COLORS.black}>
                        {column.title}
                      </Text>
                    )}
                    {hasTitles && !column.title && <div style={{ height: "24px" }} />}
                    {column.items.map(link => (
                      <LinkItemComponent key={link.id} link={link} />
                    ))}
                  </LinkColumn>
                ))}
              </LinkGrid>
            </DropdownContent>
          </DropdownWrapper>
        </Fragment>
      );
    });
  }, [activeDropdown, isScrolled, menuItems, setActiveDropdown]);

  return (
    <Wrapper as="header" mt="none" my="none" isScrolled={isScrolled}>
      <Inner
        maxWidth="1000px"
        flexDirection="row"
        mt="none"
        alignItems="center"
        justifyContent="space-between">
        <LogoLink title="Home" href="/">
          <Icon iconId="logo.full" fill="primary" />
        </LogoLink>

        <DesktopNav ref={navRef} as="nav">
          <div className="desktop-nav-items">{renderDesktopNavItems()}</div>
        </DesktopNav>

        <DesktopNav>
          <div className="desktop-nav-items">
            <CtaButtonComponent isMobile={false} />
          </div>
          <Flex className="desktop-nav-button" gap="16px">
            <MarketingButton href={MarketingButtons.getStarted.link} small>
              {MarketingButtons.getStarted.text}
            </MarketingButton>
            <MenuButton
              aria-label="Open menu"
              aria-pressed={showMenu}
              className="menu-button"
              icon={showMenu ? "action.close" : "navigation.menu"}
              iconSize={24}
              variant="blue"
              onClick={handleMobileMenuToggle}
            />
          </Flex>
        </DesktopNav>
      </Inner>
    </Wrapper>
  );
}
